/**
 * Banker session state (`backend/src/game/banker/`) — Gold <-> Cash exchange,
 * one-way (PREMIUM debited, SOFT credited, never the reverse — see
 * `EconomyConfig.values.premiumToSoftRate` and the "Gold is deposit-only, no
 * gameplay faucet" rule this respects on the backend). Mirrors
 * `vendor-state.ts`'s `createStore` + scoped `economy:updated` balance-sync
 * pattern exactly.
 */
import { createStore } from '../../core/state/store';
import { api } from '../../core/network/api';
import { socket } from '../../core/network/socket';
import { gameStateStore } from './game-state';
import type {
  BankerState, BankerDetail, BankerBalance, BankerSummary, ExchangeResult,
} from './banker-state.types';

const INITIAL_STATE: BankerState = {
  banker: null, balance: null, busy: false, error: null, lastReceipt: null,
};

export const bankerStateStore = createStore<BankerState>({ ...INITIAL_STATE });

let activeBankerKey: string | null = null;
let unsubscribeBalance: (() => void) | null = null;

function connectBalanceSync(): void {
  disconnectBalanceSync();
  const onBalanceUpdated = (balance: BankerBalance) => {
    bankerStateStore.set((s) => ({ ...s, balance }));
  };
  socket.on('economy:updated', onBalanceUpdated);
  unsubscribeBalance = () => socket.off('economy:updated', onBalanceUpdated);
}

function disconnectBalanceSync(): void {
  unsubscribeBalance?.();
  unsubscribeBalance = null;
}

/**
 * The wallet as the character sheet knows it — the same read
 * `vendor-state.ts` does, for the same reason and now for a second one.
 *
 * `balance` used to be populated ONLY by an exchange response or an
 * `economy:updated` push, so a freshly opened banker printed
 * "— Cash · — Gold" next to a HUD showing the real numbers. That was
 * cosmetic while the amount was typed into a free-form number box. It stopped
 * being cosmetic the moment the amount became a SLIDER: a slider's ceiling is
 * "all of your Gold", so an unknown balance is an undrawable control.
 *
 * Null while the sheet has not loaded. Unknown stays unknown rather than
 * rendering as 0 — a zero balance and an unread balance look identical on
 * screen but mean opposite things, and the panel renders them differently.
 */
function characterBalance(): BankerBalance | null {
  const character = gameStateStore.get().character;
  if (character?.state !== 'ACTIVE') return null;
  return { cash: character.softBalance, gold: character.premiumBalance };
}

/**
 * The first banker in the roster, for a caller that has no key.
 *
 * The HUD's Cash chip opens the exchange, and the HUD has no NPC context to get
 * a key from. Hardcoding `'halvard'` there is this repo's recorded
 * "hardcoded constants hide unreachable features" trap: the literal compiles,
 * the feature works until the seed renames the NPC, and then the button is
 * silently dead with nothing failing. `GET /banker` already returns the roster
 * — ask it.
 *
 * Returns null on an empty roster or a failed fetch; the caller hides its entry
 * point rather than opening a panel that cannot load.
 */
export async function firstBankerKey(): Promise<string | null> {
  try {
    const list = await api.get<BankerSummary[]>('/banker');
    return list[0]?.key ?? null;
  } catch {
    return null;
  }
}

/** Opens a banker panel: fetches its detail (name + exchange rate) and starts the balance push. */
export async function loadBanker(bankerKey: string): Promise<void> {
  activeBankerKey = bankerKey;
  connectBalanceSync();
  bankerStateStore.set((s) => ({
    ...s,
    busy: true,
    error: null,
    lastReceipt: null,
    // Seeded from the character sheet the HUD already paints, NOT left null —
    // see characterBalance above for why the slider depends on it.
    balance: s.balance ?? characterBalance(),
  }));
  try {
    const banker = await api.get<BankerDetail>(`/banker/${bankerKey}`);
    bankerStateStore.set((s) => ({ ...s, banker, busy: false }));
  } catch (err) {
    bankerStateStore.set((s) => ({ ...s, busy: false, error: msg(err, 'Couldn\'t open the banker.') }));
  }
}

/** One-way exchange, only ever called after the confirm modal (banker-ui.ts) — no undo. */
export async function exchange(premiumAmount: number): Promise<void> {
  if (!activeBankerKey || premiumAmount < 1) return;
  bankerStateStore.set((s) => ({ ...s, busy: true, error: null }));
  try {
    const result = await api.post<ExchangeResult>(`/banker/${activeBankerKey}/exchange`, { premiumAmount });
    bankerStateStore.set((s) => ({
      ...s,
      busy: false,
      balance: result.balance,
      lastReceipt: `Exchanged ${result.premiumSpent} Gold for ${result.softEarned} Cash.`,
    }));
    /*
     * And back into the character sheet — the same merge `refillEnergyPremium`
     * does for the other Gold drain in the game, for the same reason.
     *
     * The sheet is where `characterBalance()` above seeds the panel's opening
     * wallet, and NOTHING else refreshes it after this call: the backend emits
     * `economy:updated`, but `hud.ts`'s subscriber only repaints its own DOM
     * from the push and never writes the numbers back. Without this merge, a
     * player who exchanges, closes the panel and opens it again is re-seeded
     * from their PRE-exchange Gold, and the slider's MAX offers an amount the
     * server will reject — the invented-ceiling bug this file removed, reached
     * through a different door.
     */
    gameStateStore.set((s) => (s.character?.state === 'ACTIVE' ? {
      ...s,
      character: { ...s.character, softBalance: result.balance.cash, premiumBalance: result.balance.gold },
    } : s));
  } catch (err) {
    bankerStateStore.set((s) => ({ ...s, busy: false, error: msg(err, 'Couldn\'t complete the exchange.') }));
  }
}

export function closeBanker(): void {
  activeBankerKey = null;
  disconnectBalanceSync();
  bankerStateStore.set({ ...INITIAL_STATE });
}

function msg(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}
